import { rx } from '@shared/rx';
import {
  get,
  merge,
  mapValues,
  compact,
  includes,
  isPlainObject,
  isNil,
  set,
} from 'lodash';
import {
  FormGroup,
  FormControl,
  ValidatorFn,
  AsyncValidatorFn,
} from '@angular/forms';
import {
  getDefaultValueForField,
  normalizeDynamicProperty,
  getLocalSchemaProperties,
} from './schema-form-functions';
import {
  PredicateFn,
  IFieldPreferences,
  SchemaNode,
  IFormRules,
  FormControlType,
  EDITOR_MODE,
} from '@shared/types';
import {
  createSchemaValidators,
  createSchemaAsyncValidators,
} from './schema-form-validators';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';

/**
 * Creates a new control for a leaf node of schema.
 *
 * @param field - combined field preferences
 * @param isFieldWithoutDefaultValueInEditMode - is we need set default value in edit mode
 * @returns
 */
export const buildControl: Function = (
  field: IFieldPreferences,
  isFieldWithoutDefaultValueInEditMode: boolean = false,
  options: {
    i18nService: XVII18nService;
  },
): FormControl => {
  // TODO: remove defaultValue property. replace it with 'default' everywhere
  const defaultValue: any = get(
    field,
    'defaultValue',
    get(field, 'default', getDefaultValueForField(field)),
  );

  const validators: ValidatorFn[] = createSchemaValidators(field, options);
  const asyncValidators: AsyncValidatorFn[] =
    createSchemaAsyncValidators(field);

  return new FormControl(
    isFieldWithoutDefaultValueInEditMode ? null : defaultValue,
    validators,
    asyncValidators,
  );
};

/**
 * Builds an angular form based on provided JSON schema
 *
 * @param schema - current chema node
 * @param rules - user defined form rules
 * @param fieldsWithoutDefaultValueInEditMode - fields that we don't need provide default value in edit mode
 * @param mode - current editor mode
 * @returns
 * TODO: Fix invalid returning typings, correct type is {@link AbstractControl}
 */
export const createFormGroupFromSchema = (
  options: {
    schema?: SchemaNode;
    rules?: IFormRules;
    fieldsWithoutDefaultValueInEditMode?: string[];
    mode?: EDITOR_MODE;
    i18nService: XVII18nService;
  },
  // eslint-disable-next-line max-params
): FormGroup => {
  const {
    schema = {},
    rules = {},
    fieldsWithoutDefaultValueInEditMode = [],
    mode = null,
    i18nService,
  } = options ?? {};

  const omitFunctions: { omit: Function; control: FormControl }[] = [];

  // Appends schema' "virtual" branch with items from formRules.virtual,
  // this code will make virtual render happen

  // CAUTION!: it will overwrite existing virtual properties in schema by reference
  // so if you pass the same schema object to this function multiple times
  // it will keep appending virtual properties from formRules.virtual
  if (get(rules, 'virtual')) {
    set(
      schema,
      'properties.virtual',
      merge(
        { type: 'object', properties: {} },
        get(schema, 'properties.virtual'),
        get(rules, 'virtual'),
      ),
    );
  }

  const translatedSchema: SchemaNode =
    i18nService.translateTextInSchema(schema);
  const translatedRules: IFormRules =
    i18nService.translateTextInFormRules(rules);

  /**
   * Combines schema node and formRules for field. Creates either a leaf control or a group.
   * Creates group children recursively.
   *
   * @param field      schema node
   * @param schemaPath dot separated schema node path
   *
   * TODO: Fix invalid returning typings, correct type is {@link AbstractControl}
   */
  const buildFormNode: Function = (
    field,
    schemaPath: string = '',
  ): FormControl => {
    const isFieldWithoutDefaultValueInEditMode: boolean =
      includes(fieldsWithoutDefaultValueInEditMode, schemaPath) &&
      mode === EDITOR_MODE.EDIT;
    // the field should be treated as a group when its type is
    // object (or absent) and properties is an object
    const isNode: boolean = isNil(field.controlType)
      ? get(field, 'type', 'object') === 'object' &&
        isPlainObject(field.properties)
      : field.controlType === FormControlType.control;
    const control: FormControl = !isNode
      ? buildControl(field, isFieldWithoutDefaultValueInEditMode, options)
      : new FormGroup(
          mapValues(field.properties, (value, name) => {
            const childPath: string = compact([schemaPath, name]).join('.');

            return buildFormNode(
              merge(
                includes(field.required, name) ? { required: true } : {},
                value,
                get(translatedRules, ['fields', childPath]),
              ),
              childPath,
            );
          }),
        );

    const omit: boolean | PredicateFn = normalizeDynamicProperty(field.omit);
    if (typeof omit === 'function') {
      omitFunctions.push({ omit, control });
    } else if (omit) {
      control.disable();
    }

    return control;
  };

  // Partial schema acquired from formRules fields with "schema" property
  const localSchemaProperties = getLocalSchemaProperties(translatedRules);

  // TODO: Fix invalid returning typings, correct type is {@link AbstractControl}
  const form: FormGroup = buildFormNode(
    merge({ type: 'object' }, translatedSchema, {
      properties: localSchemaProperties,
    }),
  );

  // set root node validators. Can be passed with schema or formRules
  // may be used for completely custom value validation with dependent fields etc...
  form.setValidators(
    get(translatedRules, 'validators', get(translatedSchema, 'validators')),
  );

  if (omitFunctions.length) {
    form.valueChanges.pipe(rx.debounceTime(0)).subscribe((value) => {
      let flag = false;

      omitFunctions.forEach(({ control, omit }) => {
        if (control.disabled === omit(value, form)) return;

        flag = true;
        if (control.disabled) {
          control.enable({ emitEvent: false });
        } else {
          control.disable({ emitEvent: false });
        }
      });

      if (flag) setTimeout(() => form.updateValueAndValidity(), 1);
    });
  }

  return form;
};
